import { client, sessionSink, unwrap } from "./client";
import { ApiError } from "./errors";
import type {
  MfaEnrollment,
  MfaEnrollmentCodeSent,
  MfaRecoveryCodes,
  MfaStatus,
  MfaVerified,
  MfaVerifyBody,
} from "./types";

/**
 * xmg-sec-mfa (Xero security standard S3a, "2SA will be the minimum level"): the five two-step authentication
 * calls, and the step-up retry every sensitive action goes through.
 *
 * A password alone never finishes a sign-in. The server refuses a password-only session everywhere but these
 * routes with `403 mfa_enrollment_required` / `mfa_required`; `client.ts` hands either code to the session
 * provider, and `RequireSession` sends the user to `/auth/mfa/setup` or `/auth/mfa`.
 *
 * xmg-sec-mfa-takeover: setting up a factor on an account that has none needs a code mailed to the account's
 * address (`sendMfaEnrollmentCode`, then `startMfaEnrollment(emailCode)`), so the password alone cannot bind
 * someone else's authenticator. Proving that code, and passing the second factor for the first time, each
 * ROTATE the session: the server sets a new cookie and returns its new CSRF token, which `adoptRotatedCsrf`
 * installs before anything else sends a mutating request.
 */

export function getMfaStatus(): Promise<MfaStatus> {
  return unwrap(client.GET("/api/v1/auth/mfa", {})) as Promise<MfaStatus>;
}

/** Mails a single-use set-up code to the signed-in account's address; the code never comes back here. */
export function sendMfaEnrollmentCode(): Promise<MfaEnrollmentCodeSent> {
  return unwrap(client.POST("/api/v1/auth/mfa/enroll/email-code", {})) as Promise<MfaEnrollmentCodeSent>;
}

/** The session was rotated server-side: from now on only the new CSRF token matches the new cookie. */
function adoptRotatedCsrf(token: string | null | undefined): void {
  if (token) sessionSink.token = token;
}

/**
 * `emailCode` is the mailed set-up code; `null` only when this session already proved it (starting again).
 * Proving the code rotates the session (so a cookie planted in this browser cannot go on to enrol), and the
 * response carries the new CSRF token.
 */
export async function startMfaEnrollment(emailCode: string | null): Promise<MfaEnrollment> {
  const started = (await unwrap(
    client.POST("/api/v1/auth/mfa/enroll", { body: { email_code: emailCode } }),
  )) as MfaEnrollment;
  adoptRotatedCsrf(started.csrf_token);
  return started;
}

export async function confirmMfaEnrollment(code: string): Promise<MfaRecoveryCodes> {
  const issued = (await unwrap(
    client.POST("/api/v1/auth/mfa/enroll/confirm", { body: { code } }),
  )) as MfaRecoveryCodes;
  adoptRotatedCsrf(issued.csrf_token);
  return issued;
}

export async function verifyMfa(body: MfaVerifyBody): Promise<MfaVerified> {
  const verified = (await unwrap(client.POST("/api/v1/auth/mfa/verify", { body }))) as MfaVerified;
  adoptRotatedCsrf(verified.csrf_token);
  return verified;
}

export function regenerateRecoveryCodes(): Promise<MfaRecoveryCodes> {
  return withStepUp(
    () => unwrap(client.POST("/api/v1/auth/mfa/recovery-codes", {})) as Promise<MfaRecoveryCodes>,
  );
}

/** A code typed as `123 456` or pasted with a trailing newline is the same six digits. */
export function cleanCode(raw: string): string {
  return raw.replace(/\s+/g, "");
}

/**
 * The step-up prompt. `StepUpDialog` (mounted once, in the signed-in layout) registers itself here; it resolves
 * `true` once the user passed a code and `false` when they cancelled. Module-level for the same reason
 * `sessionSink` is: the hooks that need it are plain functions, not components.
 */
export const stepUpSink: { prompt: (() => Promise<boolean>) | null } = { prompt: null };

/**
 * Runs `call`; if the server answers `403 mfa_step_up_required` (a sensitive action - connection revoke, member
 * or role changes, sign-off - without a recent second factor), asks for a code ONCE and retries ONCE. A
 * cancelled prompt, or no prompt mounted, re-throws the original error so the screen shows it as any other.
 * The first attempt was refused before the route ran, so the retry does the work exactly once.
 */
export async function withStepUp<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    const prompt = stepUpSink.prompt;
    if (!(error instanceof ApiError) || !error.is("mfa_step_up_required") || prompt === null) {
      throw error;
    }
    const passed = await prompt();
    if (!passed) {
      throw error;
    }
    return call();
  }
}

/**
 * The recovery codes as a plain-text file, offered ONCE right after they are issued: the server keeps only a
 * hash of each, so this is the only moment the plaintext exists outside the user's hands.
 */
export function recoveryCodesFile(codes: readonly string[], account: string, issuedAt: Date): string {
  const lines = [
    `LuciMigration recovery codes for ${account}`,
    `Issued ${issuedAt.toISOString()}`,
    "",
    "Each code works once. Keep them somewhere safe and offline.",
    "",
    ...codes,
    "",
  ];
  return lines.join("\n");
}
